
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Product, Group } from "@/lib/types";
import { recordUsage, sortByUsageAndAlphabet, getProductUsageCount, getGroupUsageCount } from "@/lib/services/usageFrequencyService";

interface ProductSelectionProps {
  products: Product[];
  groups: Group[];
  selectedProductId: string;
  setSelectedProductId: (id: string) => void;
  selectedGroupId: string;
  handleGroupChange: (groupId: string) => void;
}

const ProductSelection = ({
  products,
  groups,
  selectedProductId,
  setSelectedProductId,
  selectedGroupId,
  handleGroupChange,
}: ProductSelectionProps) => {
  // Sort groups and products by usage frequency
  const sortedGroups = sortByUsageAndAlphabet(groups, 'group', (group) => 
    getGroupUsageCount(group.id, products.filter(p => p.groupIds.includes(group.id)))
  );
  
  // Filter products based on selected group
  const filteredProducts = selectedGroupId && selectedGroupId !== "all-groups"
    ? products.filter(product => product.groupIds.includes(selectedGroupId))
    : products;
  
  const sortedProducts = sortByUsageAndAlphabet(filteredProducts, 'product', (product) => getProductUsageCount(product.id));
  
  // Set the first product as selected by default (if any products exist)
  useEffect(() => {
    if (sortedProducts.length > 0 && (!selectedProductId || !sortedProducts.some(p => p.id === selectedProductId))) {
      setSelectedProductId(sortedProducts[0].id);
    }
  }, [sortedProducts, selectedProductId, setSelectedProductId]);

  const handleGroupSelect = (groupId: string) => {
    if (groupId !== "all-groups") {
      recordUsage('group', groupId);
    }
    handleGroupChange(groupId);
  };

  const handleProductSelect = (productId: string) => {
    recordUsage('product', productId);
    setSelectedProductId(productId);
  };

  return (
    <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
      {/* Group selection dropdown */}
      <Select 
        value={selectedGroupId} 
        onValueChange={handleGroupSelect}
      >
        <SelectTrigger className="w-full sm:w-[200px]">
          <SelectValue placeholder="All Groups" />
        </SelectTrigger>
        <SelectContent className="bg-background border border-border shadow-lg z-50">
          <SelectItem value="all-groups">All Groups</SelectItem>
          {sortedGroups.map((group) => (
            <SelectItem key={group.id} value={group.id}>
              {group.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {/* Product selection dropdown */}
      <Select 
        value={selectedProductId} 
        onValueChange={handleProductSelect}
      >
        <SelectTrigger className="w-full sm:w-[200px]">
          <SelectValue placeholder="Select a product" />
        </SelectTrigger>
        <SelectContent className="bg-background border border-border shadow-lg z-50">
          {sortedProducts.map((product) => (
            <SelectItem key={product.id} value={product.id}>
              {product.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ProductSelection;
